<template>
  <div v-if="!$apollo.queries.getBusinessEntityDetails.loading">
    <FlowHeading
      :title="$t('Add your business details')"
      :sub-title="
        $t('This information allows us to show your correct business information on your bills.')
      "
    />

    <div class="business-entity-name m-b-20">
      <WdsInput
        v-model="businessName"
        name="businessName"
        autocomplete="organization"
        :label="$t(`Business entity name`)"
        :error="errorBusinessName"
        placeholder="Worksome"
        required
        lg
        @focusout="v$.businessName.$touch"
      />
    </div>

    <CompanyNumberInput market="US">
      <div class="tw-mt-2">
        <WdsInput
          v-model="businessNumber"
          name="businessNumber"
          placeholder="12345678"
          :error="errorBusinessNumber"
          required
          lg
          @focusout="v$.businessNumber.$touch"
        />
      </div>
    </CompanyNumberInput>

    <div class="wds-input-group continue-button">
      <WdsButton full-width lg primary :disabled="!canSubmit" @click="callNextScreen">
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
    <div v-if="lacksRequiredFields" class="button-ghost-container">
      <WdsStack justify-center>
        <WdsButtonGhost secondary @click="skip">{{ $t('Skip and update later') }}</WdsButtonGhost>
      </WdsStack>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { WdsButton, WdsButtonGhost, WdsInput, WdsStack } from '@worksome/wds-components'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import CompanyNumberInput from '@/components/compliances/BusinessEntity/CompanyNumberInput.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import { useValidatorWithGraphQlError } from '@/composables/errors'
import { FormValidation } from '@/utils/formValidation'

export default {
  name: 'UsBusinessEntityTypeDetails',

  components: {
    ButtonSpinner,
    CompanyNumberInput,
    FlowHeading,
    WdsButton,
    WdsButtonGhost,
    WdsInput,
    WdsStack,
  },

  inject: ['skip', 'next'],

  setup() {
    const v$ = useVuelidate()
    const errors = ref({})

    const errorBusinessName = useValidatorWithGraphQlError(
      v$,
      errors,
      'businessName',
      'registered_company_name',
    )
    const errorBusinessNumber = useValidatorWithGraphQlError(
      v$,
      errors,
      'businessNumber',
      'company_no',
    )

    return {
      v$,
      errors,
      errorBusinessName,
      errorBusinessNumber,
    }
  },

  data() {
    return {
      name: 'UsBusinessEntityTypeDetails',
      businessName: '',
      businessNumber: '',
      businessDetailsFinished: false,
      globalId: '',
      isSubmitting: false,
    }
  },
  computed: {
    lacksRequiredFields() {
      return this.v$.$invalid
    },
    canSubmit() {
      return !this.isSubmitting
    },
  },

  methods: {
    async callNextScreen() {
      this.errors = {}
      this.v$.$touch()
      if (this.v$.$invalid) {
        return
      }
      this.isSubmitting = true
      await this.updateBusinessEntityDetails()
      if (Object.keys(this.errors).length === 0) {
        this.next()
      } else {
        this.isSubmitting = false
      }
    },

    async updateBusinessEntityDetails() {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation updatePrimaryBusinessEntity($input: UpdatePrimaryBusinessEntityInput!) {
              updatePrimaryBusinessEntity(input: $input) {
                globalId
                entityType
                isPrimary
                label
                companyNo
                companyName
                data
              }
            }
          `,
          variables: {
            input: {
              globalId: this.globalId, // must be accountID of current logged in user
              registered_company_name: this.businessName,
              business_entity: 'common_business',
              company_no: this.businessNumber,
            },
          },
          context: {
            headers: {
              'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
            },
          },
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },
  },

  apollo: {
    getBusinessEntityDetails: {
      query: gql`
        query me {
          me {
            activeAccount {
              globalId
              ... on Freelancer {
                primaryBusinessEntity {
                  entityType
                  isPrimary
                  label
                  companyNo
                  companyName
                  data
                }
              }
            }
          }
        }
      `,
      result({ data }) {
        const info = data.me.activeAccount.primaryBusinessEntity
        this.globalId = data.me.activeAccount.globalId
        if (info) {
          this.businessName = info.companyName ? info.companyName : ''
          this.businessNumber = info.companyNo ?? this.businessNumber
          this.businessDetailsFinished = true
        }
      },
      update: (data) => data.me,
    },
  },

  validations() {
    return {
      businessName: {
        required: helpers.withMessage($t('Please enter your business name'), required),
      },
      businessNumber: {
        required: helpers.withMessage($t('Please enter your FEIN'), required),
      },
    }
  },
}
</script>

<style scoped>
.continue-button {
  margin-top: 24px;
  margin-bottom: 16px;
}
</style>
